<template>
  <div class="calendar">
    <div class="calendar-block">
      <div class="calendar-title">
        <slot name="previous"
          ><a class="calendar-previous" @click.prevent="previous"><i class="af af-chevron-left"></i></a
        ></slot>
        <span v-if="range">{{ displayedMonthName + ' ' + displayedYear }}</span>
        <a v-else @click.prevent="decreasePrecision">{{
          mode === Select.DATE ? displayedMonthName + ' ' + displayedYear : mode === Select.MONTH ? displayedYear : null
        }}</a>
        <slot name="next"
          ><a class="calendar-next" @click.prevent="next"><i class="af af-chevron-right"></i></a
        ></slot>
      </div>

      <div class="calendar-date" v-if="mode === Select.DATE">
        <div class="calendar-week">
          <div>Sun</div>
          <div>Mon</div>
          <div>Tue</div>
          <div>Wed</div>
          <div>Thu</div>
          <div>Fri</div>
          <div>Sat</div>
        </div>
        <div class="calendar-grid">
          <div class="calendar-spacer" v-for="spacer in spacerCount" :key="spacer"></div>
          <a
            class="calendar-day"
            @click.prevent="selectDate(day)"
            @mouseover="hoverDate(day)"
            @mouseleave="hoverDate(null)"
            :class="{
              'calendar-day-active': isActive(day),
              'calendar-day-range-start': isRangeStart(day),
              'calendar-day-range-end': isRangeEnd(day),
              'calendar-day-highlight': isHighlighted(day),
              'calendar-day-disabled': !isDayEnabled(day),
            }"
            v-for="day in displayedMonthDays"
            :key="displayedYear + '+' + displayedMonth + '+' + day"
          >
            {{ day }}
          </a>
        </div>
      </div>
      <div class="calendar-month" v-if="mode === Select.MONTH">
        <a
          v-for="(month, index) in months"
          @click.prevent="selectMonth(index)"
          :class="{
            'calendar-month-disabled': !isMonthEnabled(index),
          }"
          >{{ month }}</a
        >
      </div>
      <div class="calendar-year" v-if="mode === Select.YEAR">
        <a
          v-for="yearOffset in 12"
          @click.prevent="selectYear(currentYear - 1 + yearOffset)"
          :class="{
            'calendar-year-disabled': !isYearEnabled(currentYear - 1 + yearOffset),
          }"
          >{{ currentYear - 1 + yearOffset }}</a
        >
      </div>
    </div>
  </div>
</template>
<script>
import {
  getDaysInMonth,
  format,
  getYear,
  getMonth,
  isSameDay,
  getDay,
  addMonths,
  subMonths,
  addYears,
  subYears,
  isBefore,
  isAfter,
  areIntervalsOverlapping,
  endOfYear,
  endOfMonth,
  isValid,
} from 'date-fns';

const Select = {
  YEAR: 'year',
  MONTH: 'month',
  DATE: 'date',
};

export default {
  props: {
    modelValue: {
      default: null,
      validator: (val) => !val || isValid(val) || Array.isArray(val),
    },
    displayed: {
      default: null,
      type: [Date, Number],
    },
    hovered: {
      default: null,
      type: [Date, Number],
    },
    limitStart: {
      default: null,
      type: [Date, Number],
    },
    limitEnd: {
      default: null,
      type: [Date, Number],
    },
    range: {
      default: false,
      type: Boolean,
    },
    enableDateFn: {
      default: null,
      type: Function,
    },
  },

  data() {
    return {
      currentDate: null,
      displayedDate: null,
      mode: Select.DATE,
      Select: Select,
      months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      selectedDate: null,
      selectedDateEnd: null,
      hoveredDate: null,
    };
  },

  mounted() {
    const { selectedDate, selectedDateEnd } = this.valueToSelectedDates(this.modelValue);
    this.currentDate = new Date();
    this.displayedDate = this.displayed || selectedDate || new Date();
    this.selectedDate = selectedDate;
    this.selectedDateEnd = selectedDateEnd;
  },

  methods: {
    selectDate(day) {
      if (this.isDayEnabled(day)) {
        const newDate = new Date(this.displayedYear, this.displayedMonth, day);
        if (
          !this.range ||
          isBefore(newDate, this.selectedDate) ||
          (this.range && (!this.selectedDate || (this.selectedDate && this.selectedDateEnd)))
        ) {
          this.selectedDate = newDate;
          this.selectedDateEnd = null;
        } else {
          this.selectedDateEnd = newDate;
        }
        this.$emit('update:modelValue', this.range ? [this.selectedDate, this.selectedDateEnd] : this.selectedDate);
      }
    },

    hoverDate(day) {
      if (!day) {
        this.hoveredDate = day;
        this.$emit('hovered', this.hoveredDate);
      } else if (this.isDayEnabled(day)) {
        this.hoveredDate = new Date(this.displayedYear, this.displayedMonth, day);
        this.$emit('hovered', this.hoveredDate);
      }
    },

    selectMonth(month) {
      if (this.isMonthEnabled(month)) {
        this.displayedDate = new Date(this.displayedYear, month, 1);
        this.mode = Select.DATE;
        this.$emit('displayed', this.displayedDate);
      }
    },

    selectYear(year) {
      if (this.isYearEnabled(year)) {
        this.displayedDate = new Date(year, this.displayedMonth, 1);
        this.mode = Select.MONTH;
        this.$emit('displayed', this.displayedDate);
      }
    },

    previous() {
      const date =
        this.mode === Select.DATE
          ? subMonths(this.displayedDate, 1)
          : this.mode === Select.MONTH
          ? subYears(this.displayedDate, 1)
          : subYears(this.displayedDate, 12);
      this.displayedDate = date;
      this.$emit('displayed', this.displayedDate);
    },

    next() {
      const date =
        this.mode === Select.DATE
          ? addMonths(this.displayedDate, 1)
          : this.mode === Select.MONTH
          ? addYears(this.displayedDate, 1)
          : addYears(this.displayedDate, 12);
      this.displayedDate = date;
      this.$emit('displayed', this.displayedDate);
    },

    decreasePrecision() {
      if (this.range) {
        return;
      }

      if (this.mode === Select.DATE) this.mode = Select.MONTH;
      else this.mode = Select.YEAR;
    },

    isEnabled(date) {
      if (this.limitStart && isBefore(date, this.limitStart)) {
        return false;
      }
      if (this.limitEnd && isAfter(date, this.limitEnd)) {
        return false;
      }
      return true;
    },

    isDayEnabled(day) {
      const date = new Date(this.displayedYear, this.displayedMonth, day);
      if (this.enableDateFn && !this.enableDateFn(date)) {
        return false;
      }
      return this.isEnabled(date);
    },

    isMonthEnabled(month) {
      const monthStart = new Date(this.displayedYear, month, 1);
      const monthEnd = endOfMonth(monthStart);
      if (this.limitStart && this.limitEnd) {
        const monthInterval = { start: monthStart, end: monthEnd };
        const limitInterval = { start: this.limitStart, end: this.limitEnd };
        return areIntervalsOverlapping(monthInterval, limitInterval);
      } else if (this.limitStart && isBefore(monthEnd, this.limitStart)) {
        return false;
      } else if (this.limitEnd && isAfter(monthStart, this.limitEnd)) {
        return false;
      }

      return true;
    },

    isYearEnabled(year) {
      const yearStart = new Date(year, 0, 1);
      const yearEnd = endOfYear(yearStart);
      if (this.limitStart && this.limitEnd) {
        const yearInterval = { start: yearStart, end: yearEnd };
        const limitInterval = { start: this.limitStart, end: this.limitEnd };
        return areIntervalsOverlapping(yearInterval, limitInterval);
      } else if (this.limitStart && isBefore(yearEnd, this.limitStart)) {
        return false;
      } else if (this.limitEnd && isAfter(yearStart, this.limitEnd)) {
        return false;
      }

      return true;
    },

    isHighlighted(day) {
      if (!this.selectedDate) {
        return false;
      }
      const date = new Date(this.displayedYear, this.displayedMonth, day);
      if (
        this.range &&
        this.hoveredDate &&
        !isBefore(this.hoveredDate, this.selectedDate) &&
        !isAfter(this.hoveredDate, this.selectedDateEnd) &&
        isAfter(date, this.selectedDate) &&
        isBefore(date, this.hoveredDate)
      ) {
        return true;
      }
      return false;
    },

    isActive(day) {
      if (!this.selectedDate) {
        return false;
      }

      const date = new Date(this.displayedYear, this.displayedMonth, day);
      if (
        isSameDay(date, this.selectedDate) ||
        isSameDay(date, this.selectedDateEnd) ||
        (isAfter(date, this.selectedDate) && isBefore(date, this.selectedDateEnd || this.selectedDate))
      ) {
        return true;
      }
      return false;
    },

    isRangeStart(day) {
      if (!this.range || !this.selectedDate) {
        return true;
      }
      const date = new Date(this.displayedYear, this.displayedMonth, day);
      return isAfter(date, this.selectedDateEnd) || isSameDay(date, this.selectedDate) || isBefore(date, this.selectedDate);
    },

    isRangeEnd(day) {
      if (!this.range) {
        return true;
      }
      const date = new Date(this.displayedYear, this.displayedMonth, day);
      return !isBefore(date, this.selectedDateEnd) && (isSameDay(date, this.selectedDateEnd) || isSameDay(date, this.hoveredDate));
    },

    valueToSelectedDates(value) {
      const selectedDate = value && Array.isArray(value) && value.length ? value[0] : value;
      const selectedDateEnd = this.range && value && Array.isArray(value) && value.length > 1 ? value[1] : null;

      return { selectedDate, selectedDateEnd };
    },
  },

  computed: {
    currentYear() {
      if (this.displayedDate) {
        return getYear(this.displayedDate);
      }
      return 0;
    },

    displayedMonthDays() {
      if (this.displayedDate) {
        return getDaysInMonth(this.displayedDate);
      }
      return 0;
    },

    displayedMonthName() {
      if (this.displayedDate) {
        return format(this.displayedDate, 'MMMM');
      }
      return '';
    },

    displayedYear() {
      if (this.displayedDate) {
        return getYear(this.displayedDate);
      }
      return 0;
    },

    displayedMonth() {
      if (this.displayedDate) {
        return getMonth(this.displayedDate);
      }
      return 0;
    },

    spacerCount() {
      if (this.displayedDate) {
        return getDay(new Date(this.displayedYear, this.displayedMonth, 1));
      }
      return 0;
    },
  },

  watch: {
    modelValue: {
      handler(val) {
        const { selectedDate, selectedDateEnd } = this.valueToSelectedDates(val);
        this.selectedDate = selectedDate;
        this.selectedDateEnd = selectedDateEnd;
      },
      immediate: true,
    },

    displayed: {
      handler(val) {
        this.displayedDate = val || this.selectedDate || new Date();
      },
      immediate: true,
    },

    hovered: {
      handler(val) {
        this.hoveredDate = val;
      },
      immediate: true,
    },
  },
};
</script>
<style lang="postcss">
.calendar {
  min-width: 320px;

  @media (max-width: 676px) {
    min-width: 290px;
  }

  .calendar-block {
    padding: var(--space-2);
  }

  .calendar-title {
    text-align: center;
    margin-bottom: var(--space-2);
    margin-top: var(--space-2);
    display: flex;
    justify-content: space-between;
  }

  .calendar-previous,
  .calendar-next {
    width: var(--space-6);
    padding-right: var(--space-2);
    padding-left: var(--space-2);
  }

  .calendar-grid,
  .calendar-week,
  .calendar-month,
  .calendar-year {
    display: flex;
    flex-wrap: wrap;
  }

  .calendar-week {
    font-size: var(--font-size-12);
    line-height: var(--line-height-12);
  }

  .calendar-week > *,
  .calendar-day,
  .calendar-spacer {
    flex: 1;
    flex-basis: 14.28%;
    flex-grow: 0;
    flex-shrink: 0;
    height: var(--space-10);
    line-height: var(--space-10);
    text-align: center;
  }

  .calendar-month > *,
  .calendar-year > * {
    font-family: var(--font-bold);
    flex: 1;
    flex-basis: 25%;
    flex-grow: 0;
    flex-shrink: 0;
    height: var(--space-10);
    line-height: var(--space-10);
    text-align: center;

    /* purgecss ignore */
    &:hover {
      background-color: var(--grey-ultra-light);
    }
  }

  .calendar-month {
    border-radius: 4px;

    .calendar-month-disabled {
      cursor: default;
      background-color: var(--white);
      color: var(--grey-light);
    }
  }

  .calendar-year {
    border-radius: 4px;

    .calendar-year-disabled {
      cursor: default;
      background-color: var(--white);
      color: var(--grey-light);
    }
  }

  .calendar-day {
    font-family: var(--font-bold);
    width: var(--space-6);
    border-radius: 4px;

    /* purgecss ignore */
    &:hover,
    &.calendar-day-highlight {
      background-color: var(--coral-light);
    }

    &.calendar-day-active {
      background-color: var(--accent-color);
      color: white;
    }

    /* purgecss ignore */
    &.calendar-day-active,
    &:hover,
    &.calendar-day-highlight {
      /* purgecss ignore */
      &:not(.calendar-day-range-start) {
        border-top-left-radius: 0px;
        border-bottom-left-radius: 0px;
      }

      /* purgecss ignore */
      &:not(.calendar-day-range-end) {
        border-top-right-radius: 0px;
        border-bottom-right-radius: 0px;
      }
    }

    &.calendar-day-disabled {
      cursor: default;
      background-color: var(--white);
      color: var(--grey-light);
    }
  }
}
</style>
